import { shouldClientNavigate } from '@react-aria/utils';
import * as Sentry from '@sentry/react';
import * as React from 'react';
import {
  type LinkProps as AriaLinkProps,
  LinkContext,
  RouterProvider,
} from 'react-aria-components';
import {
  type LinkProps,
  Link as RRLink,
  useHref,
  useNavigate,
} from 'react-router';

export const Link: React.FC<LinkProps> = ({ to, ...props }) => {
  const path = typeof to === 'string' ? convertHashPath(to) : to;

  return <RRLink to={path} {...props} />;
};

export function convertHashPath(route: string) {
  if (route.startsWith('/')) {
    return route;
  }

  let replacedRoute = route.replace(/^#/, '/');

  if (!replacedRoute.startsWith('/')) {
    replacedRoute = `/${replacedRoute}`;
  }

  if (replacedRoute !== route) {
    Sentry.withScope((scope) => {
      scope.setLevel('warning');
      scope.captureException(
        new Error(`Route ${route} was replaced with ${replacedRoute}`),
      );
    });
  }

  return replacedRoute;
}

export const ClientRouter: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();

  return (
    <RouterProvider navigate={navigate} useHref={useHref}>
      {children}
    </RouterProvider>
  );
};

export const PreventLinkNavigation: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // if not memo, it'll complain that we don't memoize value to context :P
  // eslint-disable-next-line @eslint-react/no-unnecessary-use-memo
  const linkContext = React.useMemo<AriaLinkProps>(
    () => ({
      onClick: (e) => {
        if (shouldClientNavigate(e.currentTarget as HTMLAnchorElement, e)) {
          e.preventDefault();
        }
      },
    }),
    [],
  );

  return <LinkContext value={linkContext}>{children}</LinkContext>;
};
